// New Project form — stepper, price range slider, smart address map
import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer, Box, Typography, IconButton, TextField, FormControl,
  InputLabel, Select, MenuItem, Button, Autocomplete, Chip
} from '@mui/material';
import InventoryKeyHighlighter from '../../../components/inventory/InventoryKeyHighlighter';
import InventoryOtherDetails from '../../../components/inventory/InventoryOtherDetails';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { defaultAddressShape } from '../../../components/inventory/CompleteAddress';
import ProjectAddress from './Projectaddress';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useInventory } from '../../../contexts/InventoryContext';
import PriceRangeSlider, { PRICE_MIN, DEFAULT_PRICE_MAX } from './PriceRangeSlider';
import { SectionHeader, SECTION_COLORS } from '../../../utils/inventory_helper';

const DRAWER_WIDTH = 500;

const getDeveloperLabel = (opt) => (typeof opt === 'string' ? opt : opt?.name ?? '');

const mkFieldStyles = (accentColor = SECTION_COLORS.green) => ({
  '& .MuiInputBase-root': { height: 40, fontSize: 12, borderRadius: 1.5 },
  '& .MuiInputLabel-root': { fontSize: 12, color: SECTION_COLORS.sub, '&.Mui-focused': { color: accentColor } },
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accentColor, borderWidth: 1.5 },
  },
  '& .MuiInputBase-input::placeholder': { fontSize: 12, opacity: 0.6 },
});

// ── Step Indicator ────────────────────────────────────────────────────────────
const StepIndicator = ({ currentStep, steps }) => (
  <Box sx={{ display: 'flex', alignItems: 'center' }}>
    {steps.map((step, idx) => {
      const isActive = idx === currentStep;
      const isComplete = idx < currentStep;
      const color = isComplete || isActive ? SECTION_COLORS.green : '#ccc';
      return (
        <React.Fragment key={idx}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}>
            <Box sx={{
              width: 26, height: 26, borderRadius: '50%',
              border: `2px solid ${color}`,
              bgcolor: isComplete ? SECTION_COLORS.green : isActive ? SECTION_COLORS.greenLight : 'transparent',
              color: isComplete ? 'white' : isActive ? SECTION_COLORS.green : '#aaa',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700, transition: 'all 0.25s ease',
            }}>
              {isComplete ? <CheckCircleOutlineIcon sx={{ fontSize: 14 }} /> : idx + 1}
            </Box>
            <Typography sx={{
              fontSize: 9.5, fontWeight: isActive ? 700 : 400,
              color: isActive ? SECTION_COLORS.green : isComplete ? SECTION_COLORS.green : '#aaa',
              whiteSpace: 'nowrap', transition: 'all 0.25s ease',
            }}>
              {step}
            </Typography>
          </Box>
          {idx < steps.length - 1 && (
            <Box sx={{
              height: 2, flex: 1, mx: 0.5, mb: 2.4,
              bgcolor: idx < currentStep ? SECTION_COLORS.green : '#e0e0e0',
              transition: 'background-color 0.3s ease',
            }} />
          )}
        </React.Fragment>
      );
    })}
  </Box>
);


// ─── Main Component ───────────────────────────────────────────────────────────
const AddNewProject = ({ open, onClose, onSubmit, initialDeveloper = null }) => {
  const { developers, inventoryOptions } = useInventory();
  const developerOptions = (inventoryOptions?.developers?.length ? inventoryOptions.developers : developers) || [];
  const constructionStatuses = inventoryOptions?.construction_statuses ?? [];
  const areaUnits = inventoryOptions?.area_units ?? [];
  const projectTagSuggestions = inventoryOptions?.project_tag_suggestions ?? [];
  const constructionStatusDatesRules = inventoryOptions?.construction_status_dates_rules ?? {};

  const defaultStatus = constructionStatuses.find((c) => c.value === 'under_construction')?.value ?? constructionStatuses[0]?.value ?? 'under_construction';
  const defaultAreaUnit = areaUnits.find((a) => a.value === 'sqft')?.value ?? areaUnits[0]?.value ?? 'sqft';

  const STEPS = ['Project Details', 'Location'];
  const [currentStep, setCurrentStep] = useState(0);

  const [formData, setFormData] = useState({
    developer: null,
    name: '',
    description: '',
    address: { ...defaultAddressShape },
    status: 'under_construction',
    possessionDate: null,
    launchDate: null,
    completionDate: null,
    reraNumber: '',
    commissionRate: '',
    priceRange: [PRICE_MIN, DEFAULT_PRICE_MAX],
    totalArea: '',
    areaUnit: 'sqft',
    keyHighlights: [''],
    otherDetails: [{ label: '', value: '' }],
    tags: [],
  });

  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const thumbnailInputRef = useRef(null);
  const [developerInputValue, setDeveloperInputValue] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  /** Get date field rules for the current construction status (visible, required, label per launch_date, possession_date, completion_date). */
  const getDateRulesForStatus = (status) => {
    const key = (status || '').toString().trim().toLowerCase().replace(/\s+/g, '_');
    const rules = constructionStatusDatesRules[key] || {};
    return {
      launch_date: rules.launch_date ?? { visible: false, required: false, label: 'Launch Date' },
      possession_date: rules.possession_date ?? { visible: false, required: false, label: 'Possession Date' },
      completion_date: rules.completion_date ?? { visible: false, required: false, label: 'Completion Date' },
    };
  };

  const dateRules = getDateRulesForStatus(formData.status);

  const fs = mkFieldStyles(SECTION_COLORS.green);

  const resetForm = () => {
    setDeveloperInputValue('');
    setCurrentStep(0);
    setFormData({
      developer: null,
      name: '',
      description: '',
      address: { ...defaultAddressShape },
      status: defaultStatus,
      possessionDate: null,
      launchDate: null,
      completionDate: null,
      reraNumber: '',
      commissionRate: '',
      priceRange: [PRICE_MIN, DEFAULT_PRICE_MAX],
      totalArea: '',
      areaUnit: defaultAreaUnit,
      keyHighlights: [''],
      otherDetails: [{ label: '', value: '' }],
      tags: [],
    });
    setThumbnailFile(null);
    if (thumbnailPreview && thumbnailPreview.startsWith('blob:')) URL.revokeObjectURL(thumbnailPreview);
    setThumbnailPreview('');
    setErrors({});
  };

  useEffect(() => {
    if (!open) resetForm();
    else if (initialDeveloper) {
      setFormData((prev) => ({ ...prev, developer: initialDeveloper }));
      setDeveloperInputValue(initialDeveloper?.name ?? '');
    }
  }, [open, initialDeveloper]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev, [field]: null };
      if (field === 'status') {
        next.launchDate = null;
        next.possessionDate = null;
        next.completionDate = null;
      }
      return next;
    });
  };

  const validateStep1 = () => {
    const next = {};
    const dev = formData.developer;
    const typed = (developerInputValue || '').trim();
    const hasDev = (dev != null && (typeof dev === 'string' ? dev.trim() : dev?.name)) || typed.length > 0;
    if (!hasDev) next.developer = 'Developer is required';
    if (!(formData.name || '').trim()) next.name = 'Project name is required';
    if (!(formData.status || '').trim()) next.status = 'Construction status is required';
    const rate = formData.commissionRate;
    if (!rate || String(rate).trim() === '' || isNaN(parseFloat(rate))) next.commissionRate = 'Commission % is required';
    else if (parseFloat(rate) > 15) next.commissionRate = 'Commission cannot exceed 15%';
    const ta = parseFloat(formData.totalArea);
    if (!formData.totalArea || String(formData.totalArea).trim() === '') next.totalArea = 'Total area is required';
    else if (!isNaN(ta) && ta <= 0) next.totalArea = 'Total area must be > 0';
    if (!(formData.areaUnit || '').trim()) next.areaUnit = 'Area unit is required';
    const pMin = formData.priceRange?.[0];
    const pMax = formData.priceRange?.[1];
    if (pMin != null && pMax != null && pMin > pMax) {
      next.priceRange = 'Min price cannot be greater than max price';
    }
    // Construction status date rules: validate required dates for current status
    if (dateRules.launch_date.visible && dateRules.launch_date.required) {
      const v = formData.launchDate;
      if (v == null || (dayjs.isDayjs(v) && !v.isValid())) next.launchDate = dateRules.launch_date.label + ' is required';
    }
    if (dateRules.possession_date.visible && dateRules.possession_date.required) {
      const v = formData.possessionDate;
      if (v == null || (dayjs.isDayjs(v) && !v.isValid())) next.possessionDate = dateRules.possession_date.label + ' is required';
    }
    if (dateRules.completion_date.visible && dateRules.completion_date.required) {
      const v = formData.completionDate;
      if (v == null || (dayjs.isDayjs(v) && !v.isValid())) next.completionDate = dateRules.completion_date.label + ' is required';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const validateStep2 = () => {
    const next = {};
    if (!(formData.address?.city || '').trim()) next.city = 'City is required';
    if (!(formData.address?.google_maps_url || '').trim()) next.google_maps_url = 'Google Maps link is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleNext = () => { if (currentStep === 0 && validateStep1()) setCurrentStep(1); };
  const handleBack = () => { if (currentStep > 0) setCurrentStep((s) => s - 1); };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    const dev = formData.developer;
    const typed = (developerInputValue || '').trim();
    const isExisting = dev && typeof dev === 'object' && dev.id;
    const newName = typeof dev === 'string' && dev.trim() ? dev.trim() : (typed || null);

    const toDateStr = (d) => (d == null ? null : (dayjs.isDayjs(d) ? d : dayjs(d)).format('YYYY-MM-DD'));
    const metadataFromOther = (formData.otherDetails || [])
      .filter((row) => (row.label || '').trim())
      .reduce((acc, { label, value }) => ({ ...acc, [(label || '').trim()]: (value || '').trim() }), {});
    const payload = {
      developer_id: isExisting ? dev.id : null,
      developer_name: isExisting ? null : newName,
      name: formData.name.trim(),
      description: (formData.description || '').trim() || null,
      status: formData.status,
      ...(thumbnailFile ? { thumbnailFile } : {}),
      launch_date: toDateStr(formData.launchDate),
      possession_date: toDateStr(formData.possessionDate),
      completion_date: toDateStr(formData.completionDate),
      rera_no: formData.reraNumber.trim() || null,
      commission_rate: formData.commissionRate ? parseFloat(formData.commissionRate) : null,
      commission_type: 'percentage',
      price_min: formData.priceRange[0] ?? PRICE_MIN,
      price_max: formData.priceRange[1] ?? DEFAULT_PRICE_MAX,
      total_area: formData.totalArea ? parseFloat(formData.totalArea) : null,
      area_unit: formData.areaUnit || 'sqft',
      tags: (formData.tags || []).map((t) => String(t).trim()).filter(Boolean),
      key_highlights: (formData.keyHighlights || []).map((h) => String(h).trim()).filter(Boolean),
      metadata: Object.keys(metadataFromOther).length ? metadataFromOther : {},
      address: {
        address_line: (formData.address?.address_line || '').trim() || null,
        locality:     (formData.address?.locality || '').trim() || null,
        city:         (formData.address?.city || '').trim() || null,
        state:        (formData.address?.state || '').trim() || null,
        country:      (formData.address?.country || 'IN').trim() || 'IN',
        pincode:      (formData.address?.pincode || '').trim() || null,
        latitude:     formData.address?.latitude ?? null,
        longitude:    formData.address?.longitude ?? null,
        google_place_id: (formData.address?.google_place_id || '').trim() || null,
        google_maps_url: (formData.address?.google_maps_url || '').trim() || null,
      },
    };

    setSubmitting(true);
    try { await onSubmit?.(payload); onClose(); }
    finally { setSubmitting(false); }
  };

  const Divider = () => <Box sx={{ height: 1, bgcolor: '#f0f0f0' }} />;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ backdrop: { invisible: false } }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: DRAWER_WIDTH },
          maxWidth: '100%',
          boxShadow: '-6px 0 32px rgba(0,0,0,0.14)',
          display: 'flex', flexDirection: 'column',
        },
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>

        {/* Header */}
        <Box sx={{ px: 2.5, pt: 2, pb: 1.75, bgcolor: `${SECTION_COLORS.green}0D`, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: SECTION_COLORS.green, lineHeight: 1.2 }}>New Project</Typography>
              <Typography sx={{ fontSize: 11.5, color: SECTION_COLORS.sub, mt: 0.3 }}>Fill what you know — update the rest anytime</Typography>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' }, mt: 0.25 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <StepIndicator currentStep={currentStep} steps={STEPS} />
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.75 }}>

          {/* ═══ STEP 1 ══════════════════════════════════════════ */}
          {currentStep === 0 && (
            <>
              {/* Developer */}
              <Box>
                <SectionHeader title="Developer" subtitle="Who is building this project?" accent="developer" complete={!!formData.developer} />
                <Autocomplete
                  freeSolo options={developerOptions}
                  value={formData.developer} inputValue={developerInputValue}
                  onInputChange={(_, v) => setDeveloperInputValue(v ?? '')}
                  onChange={(_, v) => { handleChange('developer', v ?? null); setDeveloperInputValue(v ? getDeveloperLabel(v) : ''); }}
                  onBlur={() => { const t = (developerInputValue || '').trim(); if (t && !formData.developer) handleChange('developer', t); }}
                  getOptionLabel={getDeveloperLabel}
                  isOptionEqualToValue={(o, v) => { if (!v) return false; if (typeof v === 'string') return (o?.name ?? '') === v; return (o?.id ?? '') === (v?.id ?? ''); }}
                  renderInput={(params) => (
                    <TextField {...params} label="Developer" placeholder="Choose or type a new developer name"
                      error={!!errors.developer} helperText={errors.developer}
                      sx={mkFieldStyles(SECTION_COLORS.blue)} size="small" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Box>

              <Divider />

              {/* Name */}
              <Box>
                <SectionHeader title="Name" subtitle="Project name and description" accent="info" complete={!!formData.name} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  <TextField fullWidth required label="Project name" placeholder="e.g. Lodha Bellagio Phase 2"
                    value={formData.name} onChange={(e) => handleChange('name', e.target.value)}
                    error={!!errors.name} helperText={errors.name} sx={mkFieldStyles(SECTION_COLORS.green)} size="small" />
                  <TextField fullWidth label="Description" placeholder="Brief description (optional)"
                    value={formData.description} onChange={(e) => handleChange('description', e.target.value)}
                    multiline minRows={2}
                    sx={{ ...mkFieldStyles(SECTION_COLORS.green), '& .MuiInputBase-root': { height: 'auto', fontSize: 12, borderRadius: 1.5 } }}
                    size="small" />
                </Box>
              </Box>

              <Divider />

              {/* Construction status */}
              <Box>
                <SectionHeader title="Construction status" accent="info" complete={!!formData.status} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  <FormControl fullWidth sx={mkFieldStyles(SECTION_COLORS.green)} size="small" error={!!errors.status}>
                    <InputLabel>Construction status</InputLabel>
                    <Select value={formData.status || defaultStatus} onChange={(e) => handleChange('status', e.target.value)} label="Construction status">
                      {(constructionStatuses.length ? constructionStatuses : [{ value: formData.status || defaultStatus, label: formData.status || defaultStatus }]).map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <Divider />

              {/* Key dates — driven by construction_status_dates_rules */}
              <Box>
                <SectionHeader
                  title="Key dates"
                  accent="dates"
                  complete={
                    (!dateRules.launch_date.required || (formData.launchDate != null && (!dayjs.isDayjs(formData.launchDate) || formData.launchDate.isValid()))) &&
                    (!dateRules.possession_date.required || (formData.possessionDate != null && (!dayjs.isDayjs(formData.possessionDate) || formData.possessionDate.isValid()))) &&
                    (!dateRules.completion_date.required || (formData.completionDate != null && (!dayjs.isDayjs(formData.completionDate) || formData.completionDate.isValid())))
                  }
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {dateRules.launch_date.visible && (
                    <DatePicker
                      label={dateRules.launch_date.label}
                      value={formData.launchDate}
                      onChange={(d) => handleChange('launchDate', d ?? null)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          sx: mkFieldStyles(SECTION_COLORS.amber),
                          error: !!errors.launchDate,
                          helperText: errors.launchDate,
                          required: dateRules.launch_date.required,
                        },
                      }}
                    />
                  )}
                  {dateRules.possession_date.visible && (
                    <DatePicker
                      label={dateRules.possession_date.label}
                      value={formData.possessionDate}
                      onChange={(d) => handleChange('possessionDate', d ?? null)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          sx: mkFieldStyles(SECTION_COLORS.amber),
                          error: !!errors.possessionDate,
                          helperText: errors.possessionDate,
                          required: dateRules.possession_date.required,
                        },
                      }}
                    />
                  )}
                  {dateRules.completion_date.visible && (
                    <DatePicker
                      label={dateRules.completion_date.label}
                      value={formData.completionDate}
                      onChange={(d) => handleChange('completionDate', d ?? null)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          sx: mkFieldStyles(SECTION_COLORS.amber),
                          error: !!errors.completionDate,
                          helperText: errors.completionDate,
                          required: dateRules.completion_date.required,
                        },
                      }}
                    />
                  )}
                </Box>
              </Box>

              <Divider />

              {/* Price Range */}
              <Box>
                <SectionHeader title="Price range" subtitle="Drag slider to set min–max price" accent="pricing" />
                <Box sx={{ border: `0.5px solid ${errors.priceRange ? 'error.main' : `${SECTION_COLORS.green}30`}`, borderRadius: 2, p: 1.75, bgcolor: SECTION_COLORS.greenLight, mb: 1.25 }}>
                  <PriceRangeSlider value={formData.priceRange} onChange={(v) => handleChange('priceRange', v)} error={errors.priceRange} />
                </Box>
              </Box>

              <Divider />

              {/* Area Range */}
              <Box>
                <SectionHeader title="Area range" accent="pricing" complete={!!(formData.totalArea && formData.areaUnit)} />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
                  <TextField fullWidth required label="Total area" placeholder="e.g. 500"
                    type="number" value={formData.totalArea}
                    onChange={(e) => handleChange('totalArea', e.target.value)}
                    error={!!errors.totalArea} helperText={errors.totalArea}
                    sx={mkFieldStyles(SECTION_COLORS.amber)} size="small" inputProps={{ min: 0, step: 0.01 }} />
                  <FormControl fullWidth sx={mkFieldStyles(SECTION_COLORS.amber)} size="small" required error={!!errors.areaUnit}>
                    <InputLabel>Area unit</InputLabel>
                    <Select value={formData.areaUnit || defaultAreaUnit} onChange={(e) => handleChange('areaUnit', e.target.value)} label="Area unit">
                      {(areaUnits.length ? areaUnits : [{ value: formData.areaUnit || defaultAreaUnit, label: formData.areaUnit || defaultAreaUnit }]).map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                    {errors.areaUnit && <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>{errors.areaUnit}</Typography>}
                  </FormControl>
                </Box>
                <Typography sx={{ fontSize: 12, color: SECTION_COLORS.sub, mt: 1, fontWeight: 600 }}>
                  Total Area: {formData.totalArea !== '' && formData.totalArea != null && !isNaN(parseFloat(formData.totalArea))
                    ? `${Number(formData.totalArea).toLocaleString('en-IN')} ${formData.areaUnit || defaultAreaUnit}`
                    : `— ${formData.areaUnit || defaultAreaUnit}`}
                </Typography>
              </Box>

              <Divider />

              {/* Commission */}
              <Box>
                <SectionHeader title="Commission rate" subtitle="Default rate applied to all configurations" accent="commission" complete={!!formData.commissionRate} />
                <TextField fullWidth required label="Commission rate (%)" placeholder="e.g. 2.5"
                  type="number" value={formData.commissionRate}
                  onChange={(e) => handleChange('commissionRate', e.target.value)}
                  error={!!errors.commissionRate} helperText={errors.commissionRate}
                  sx={{ ...mkFieldStyles(SECTION_COLORS.purple), maxWidth: 200 }}
                  size="small" inputProps={{ min: 0, max: 15, step: 0.1 }} />
              </Box>

              <Divider />

              {/* Key highlighter */}
              <Box>
                <SectionHeader 
                   title="Key highlighter"
                   subtitle="Optional highlights for this project"
                   accent="highlights" 
                   />
                <InventoryKeyHighlighter value={formData.keyHighlights} onChange={(v) => handleChange('keyHighlights', v)} placeholder="e.g. best in class amenities" />
              </Box>

              <Divider />

              {/* Tags */}
              <Box>
                <SectionHeader title="Tags" subtitle="Optional labels for search & filtering" accent="tags" />
                <Autocomplete
                  multiple freeSolo
                  options={projectTagSuggestions}
                  value={formData.tags || []}
                  onChange={(_, v) => handleChange('tags', v || [])}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : o?.label ?? '')}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Add tags (e.g. Residential, 3 BHK)" size="small"
                      sx={{ ...mkFieldStyles(SECTION_COLORS.teal), '& .MuiInputBase-root': { height: 'auto', fontSize: 12, borderRadius: 1.5, minHeight: 40 } }} />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option} label={option} size="small"
                        sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, bgcolor: SECTION_COLORS.tealLight, color: SECTION_COLORS.teal, border: `1px solid ${SECTION_COLORS.teal}30`, '& .MuiChip-deleteIcon': { fontSize: 13 } }} />
                    ))
                  }
                />
              </Box>

              <Divider />

              <InventoryOtherDetails
                value={formData.otherDetails}
                onChange={(v) => handleChange('otherDetails', v)}
              />
            </>
          )}

          {/* ═══ STEP 2 ══════════════════════════════════════════ */}
          {currentStep === 1 && (
            <Box>
              <SectionHeader
                title="Project location"
                subtitle="Search or drop a pin → address fields auto-fill. Google Maps link is required."
                accent="location"
                complete={!!(formData.address?.city || '').trim() && !!(formData.address?.google_maps_url || '').trim()}
              />
              <ProjectAddress
                value={formData.address}
                onChange={(addr) => setFormData((prev) => ({ ...prev, address: addr }))}
                error={errors?.city || errors?.google_maps_url}
              />
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#fafafa' }}>
          <Button
            variant="outlined"
            onClick={currentStep === 0 ? onClose : handleBack}
            startIcon={currentStep === 1 ? <ArrowBackIcon sx={{ fontSize: 15 }} /> : undefined}
            sx={{ color: SECTION_COLORS.green, borderColor: `${SECTION_COLORS.green}55`, textTransform: 'none', fontWeight: 600, fontSize: 12, height: 36, bgcolor: SECTION_COLORS.greenBg, '&:hover': { borderColor: SECTION_COLORS.green, bgcolor: SECTION_COLORS.greenLight } }}
          >
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {STEPS.map((_, idx) => (
              <Box key={idx} sx={{
                width: idx === currentStep ? 20 : 6, height: 6, borderRadius: 3,
                bgcolor: idx === currentStep ? SECTION_COLORS.green : idx < currentStep ? `${SECTION_COLORS.green}55` : '#ddd',
                transition: 'all 0.3s ease',
              }} />
            ))}
          </Box>

          <Button
            variant="contained"
            onClick={currentStep === 0 ? handleNext : handleSubmit}
            disabled={submitting}
            endIcon={<ArrowForwardIcon sx={{ fontSize: 15 }} />}
            sx={{ bgcolor: SECTION_COLORS.green, textTransform: 'none', fontWeight: 700, fontSize: 12, height: 36, px: 2.5, boxShadow: `0 2px 8px ${SECTION_COLORS.greenLight}`, '&:hover': { bgcolor: '#1b5e20' } }}
          >
            {currentStep === 0 ? 'Next: Location' : 'Create Project'}
          </Button>
        </Box>
      </LocalizationProvider>
    </Drawer>
  );
};

export default AddNewProject;
